/**
 * Permission Hooks
 * Provides access to user roles and fine-grained permissions
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMsal } from "@azure/msal-react";

import type { AppRole } from "./authConfig";
import useApiClient from "../hooks/useApiClient";
import { useDevBypass, devAccount, getUserRoles } from "../utils/devBypass";

interface UserPermissions {
  [toolId: string]: string[];
}

interface UsePermissionsReturn {
  /** User's Entra ID roles */
  roles: string[];
  /** Fine-grained permissions per tool */
  permissions: UserPermissions;
  /** Check if user has a specific Entra ID role */
  hasRole: (role: AppRole | string) => boolean;
  /** Check if user has any of the specified roles */
  hasAnyRole: (roles: (AppRole | string)[]) => boolean;
  /** Check if user has all of the specified roles */
  hasAllRoles: (roles: (AppRole | string)[]) => boolean;
  /** Check if user has a specific permission for a tool */
  hasPermission: (toolId: string, permission: string) => boolean;
  /** Check if user has any of the specified permissions for a tool */
  hasAnyPermission: (toolId: string, permissions: string[]) => boolean;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
}

/**
 * usePermissions Hook
 * Provides role and permission checking functionality
 *
 * @example
 * const { hasRole, hasPermission } = usePermissions();
 *
 * if (hasRole("Portal.Admin")) {
 *   // Show admin features
 * }
 *
 * if (hasPermission("inventory", "edit")) {
 *   // Show edit button
 * }
 */
export function usePermissions(): UsePermissionsReturn {
  const api = useApiClient();
  const { accounts } = useMsal();

  // Get the current account - use dev mock or real MSAL account
  const account = useDevBypass ? devAccount : accounts[0];

  // Get roles from the current account's ID token
  const roles = useMemo(() => {
    return getUserRoles(account);
  }, [account]);

  // Fetch fine-grained permissions from Permission Service
  const {
    data: permissions = {},
    isLoading,
    error,
  } = useQuery<UserPermissions>({
    queryKey: ["user-permissions"],
    queryFn: async () => {
      try {
        const response = await api.get<{ permissions: UserPermissions }>("/permissions/user/me");
        return response.permissions || {};
      } catch (err) {
        console.warn("Failed to fetch permissions:", err);
        // If API not available, return empty permissions (allow all for now)
        return {};
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: useDevBypass || !!account,
  });

  // Role checking functions
  const hasRole = (role: AppRole | string): boolean => {
    return roles.includes(role);
  };

  const hasAnyRole = (checkRoles: (AppRole | string)[]): boolean => {
    return checkRoles.some((role) => roles.includes(role));
  };

  const hasAllRoles = (checkRoles: (AppRole | string)[]): boolean => {
    return checkRoles.every((role) => roles.includes(role));
  };

  // Permission checking functions
  const hasPermission = (toolId: string, permission: string): boolean => {
    const toolPermissions = permissions[toolId];
    if (!toolPermissions) return false;
    return toolPermissions.includes(permission);
  };

  const hasAnyPermission = (toolId: string, checkPermissions: string[]): boolean => {
    const toolPermissions = permissions[toolId];
    if (!toolPermissions) return false;
    return checkPermissions.some((perm) => toolPermissions.includes(perm));
  };

  return {
    roles,
    permissions,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    hasPermission,
    hasAnyPermission,
    isLoading,
    error: error as Error | null,
  };
}

export default usePermissions;
